import type { IssuedRevisionsApi, IssueRecord } from "./issues";
import { FilesError, type ProjectFile } from "./types";

/**
 * The issue calls for files kept in this browser, which have no drawing index: an issue freezes every DXF and DWG in
 * the project at its latest version, as the service does for the xrefs a drawing has. Records live in memory, so
 * nothing is kept across a reload, and the DXF is an empty drawing: the PDF needs the service.
 */

const XREF_FILE = /\.(dxf|dwg)$/i;
const EMPTY_DXF = "data:image/vnd.dxf;charset=utf-8,0%0AEOF%0A";
const PDF_NEEDS_SERVICE = "The issued PDF needs the Orthos service.";
const ISSUED_BY = "You";

export interface LocalIssuesOptions {
  readonly list: (projectId: string) => Promise<ProjectFile[]>;
  readonly now: () => Date;
}

export function createLocalIssues(options: LocalIssuesOptions): IssuedRevisionsApi {
  const records = new Map<string, IssueRecord[]>();
  const key = (project: string, drawing: string): string => `${project}/${drawing}`;
  return {
    async freezeIssue(projectId, drawingId, body) {
      const known = records.get(key(projectId, drawingId)) ?? [];
      const first = known.find((record) => record.issue === body.issue);
      if (first !== undefined) return first;
      const files = (await options.list(projectId)).filter((file) => XREF_FILE.test(file.name));
      const record: IssueRecord = {
        issue: body.issue,
        label: body.label,
        drawingVersion: 1,
        issuedBy: ISSUED_BY,
        issuedAt: options.now().toISOString(),
        xrefs: files.map((file) => ({
          name: file.name.replace(XREF_FILE, ""),
          fileId: file.id,
          version: file.version,
          fileName: file.name,
        })),
      };
      records.set(key(projectId, drawingId), [record, ...known]);
      return record;
    },
    drawingIssues: (projectId, drawingId) =>
      Promise.resolve(records.get(key(projectId, drawingId)) ?? []),
    issuedDxfUrl: () => EMPTY_DXF,
    issuedPdf: () => Promise.reject(new FilesError("unavailable", PDF_NEEDS_SERVICE)),
  };
}
